<template>
  <div class="posts-widget">
    <!-- Remove the post tabs from here since they'll be in the parent -->
    <div v-if="loading && !fetchingMore" class="posts-loading">Loading posts...</div>
    <div v-else class="list-container" ref="listContainer">
      <div v-if="error" class="posts-error">{{ error }}</div>
      <ul v-else class="posts-list">
        <li v-for="post in filteredPosts" :key="post.id" class="post-item">
            <router-link :to="`/${post.type || 'post'}/${post.slug}`" class="post-link">
            <div class="post-content">
              <!-- Left: Icon and title -->
              <div class="post-left">
                <div class="post-icon">
                  <i :class="getPostTypeIcon(post.type)"></i>
                </div>
                <div class="post-text">
                  <h3 class="post-title" v-if="post.title && post.title.rendered">
                    {{ post.title.rendered }}
                  </h3>
                  <div class="post-meta">
                    <span v-if="post._embedded && post._embedded.author && post._embedded.author[0]">
                      By {{ post._embedded.author[0].name }}
                    </span>
                  </div>
                </div>
              </div>
              
              <!-- Middle: Post type -->
              <div class="post-type-badge">
                {{ getPostTypeName(post.type) }}
              </div>
              
              <!-- Right: View button -->
              <div class="post-right">
                <span class="view-button">View <i class="fas fa-chevron-right" style="margin-bottom: 1px; font-size: 75%;"></i></span>
              </div>
            </div>
            </router-link>
        </li>
      </ul>
      <div v-if="fetchingMore" class="load-more-indicator">
        <span>Loading more posts...</span>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchCurrentUser, fetchPosts, fetchCategoriesBySlugs, category_from_role } from '@/utils/fetch.js';

export default {
  props: {
    // Add a prop to receive the selected tab from parent
    selectedType: {
      type: String,
      default: 'all'
    }
  },
  data() {
    return {
      loading: true,
      error: null,
      allPosts: [],
      filteredPosts: [],
      // Remove selectedPostType from data since it will come from props
      postTypes: ['post', 'announcement', 'process', 'training'],
      perPage: 7,
      page: 1,
      hasMore: true,
      fetchingMore: false,
      userCategoryIds: [],
    };
  },
  methods: {
    async loadPosts() {
      this.loading = true;
      this.error = null;
      this.page = 1;

      const CACHE_KEY = 'homePostsCache';
      const CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes

      // Try cache first
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { allPosts, userCategoryIds, timestamp, selectedType } = JSON.parse(cached);
        // Only use cache if not expired, user categories match, and selectedType matches
        if (
          Date.now() - timestamp < CACHE_EXPIRY &&
          JSON.stringify(this.userCategoryIds) === JSON.stringify(userCategoryIds) &&
          this.selectedType === selectedType
        ) {
          this.allPosts = allPosts;
          this.filterPostsByType();
          this.hasMore = this.allPosts.length === this.perPage;
          this.page += 1;
          this.loading = false;
          return;
        }
      }

      try {
        // Get user for permission-based filtering
        const user = await fetchCurrentUser();
        const roles = user.my_roles || [];
        const roleCategorySlugs = roles.map(role => category_from_role[role]).filter(Boolean);

        // Get allowed category IDs directly from slugs
        let allowedCategories = [];
        if (roleCategorySlugs.length) {
          const categories = await fetchCategoriesBySlugs(roleCategorySlugs);
          allowedCategories = categories.map(cat => cat.id);
        }
        this.userCategoryIds = allowedCategories;

        // Build query string
        let query = `&per_page=${this.perPage}&page=${this.page}&_embed`;
        if (roleCategorySlugs.length) {
          query += `&categories_slug=${roleCategorySlugs.join(',')}`;
        }

        // Fetch initial posts
        this.allPosts = await fetchPosts(query, this.postTypes);

        // Apply initial filter
        this.filterPostsByType();

        // Update pagination info
        this.hasMore = this.allPosts.length === this.perPage;
        this.page += 1;

        // Cache the result
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            allPosts: this.allPosts,
            userCategoryIds: this.userCategoryIds,
            timestamp: Date.now(),
            selectedType: this.selectedType
          })
        );

      } catch (err) {
        this.error = `Failed to fetch posts: ${err.message}`;
      } finally {
        this.loading = false;
      }
    },
    
    async loadMorePosts() {
      if (this.fetchingMore || !this.hasMore) return;
      
      this.fetchingMore = true;
      
      try {
        // Get user for permission-based filtering
        const user = await fetchCurrentUser();
        const roles = user.my_roles || [];
        const roleCategorySlugs = roles.map(role => category_from_role[role]).filter(Boolean);
        
        // Build query string
        let query = `&per_page=${this.perPage}&page=${this.page}&_embed`;
        if (roleCategorySlugs.length) {
          query += `&categories_slug=${roleCategorySlugs.join(',')}`;
        }
        
        // Fetch more posts
        const morePosts = await fetchPosts(query, this.postTypes);
        
        // Add new posts to the collection
        this.allPosts = [...this.allPosts, ...morePosts];
        
        // Re-filter to update the view
        this.filterPostsByType();
        
        // Update pagination info
        this.hasMore = morePosts.length === this.perPage;
        this.page += 1;
        
      } catch (err) {
        this.error = `Failed to fetch more posts: ${err.message}`;
      } finally {
        this.fetchingMore = false;
      }
    },
    
    handleScroll() {
      if (!this.$refs.listContainer) return;
      
      const container = this.$refs.listContainer;
      const scrollPosition = container.scrollTop + container.clientHeight;
      const scrollThreshold = container.scrollHeight - 200; // Load more when within 200px of bottom
      
      if (scrollPosition >= scrollThreshold && this.hasMore && !this.fetchingMore) {
        this.loadMorePosts();
      }
    },
    
    filterPostsByType() {
      // Always include posts in user's allowed categories, or with no categories, or with "general" category
      const GENERAL_CATEGORY_ID = 1; // Change if your "general" category ID is different
      let postsInAllowedCategories = this.allPosts.filter(post =>
        !Array.isArray(post.categories) ||
        post.categories.length === 0 ||
        post.categories.includes(GENERAL_CATEGORY_ID) ||
        post.categories.some(catId => this.userCategoryIds.includes(catId))
      );
      if (this.selectedType === 'all') {
        this.filteredPosts = postsInAllowedCategories;
      } else {
        this.filteredPosts = postsInAllowedCategories.filter(post => post.type === this.selectedType);
      }
    },
    
    // Replace selectPostType with a method that emits to parent
    updatePostType(type) {
      this.$emit('update:selectedType', type);
    },
    
    getPostTypeIcon(type) {
      switch (type) {
        case 'post':
          return 'fa-light fa-file-alt';
        case 'announcement':
          return 'fa-light fa-megaphone';
        case 'process':
          return 'fa-light fa-clipboard-list-check';
        case 'training':
          return 'fa-light fa-screen-users';
        default:
          return 'fa-light fa-file-alt';
      }
    },
    
    getPostTypeName(type) {
      switch (type) {
        case 'post':
          return 'Post';
        case 'announcement':
          return 'Announcement';
        case 'process':
          return 'Process';
        case 'training':
          return 'Training';
        default:
          return 'Post';
      }
    }
  },
  mounted() {
    this.loadPosts();
    // Add scroll event listener
    if (this.$refs.listContainer) {
      this.$refs.listContainer.addEventListener('scroll', this.handleScroll);
    }
  },
  beforeUnmount() {
    // Remove scroll event listener when component is unmounted
    if (this.$refs.listContainer) {
      this.$refs.listContainer.removeEventListener('scroll', this.handleScroll);
    }
  },
  updated() {
    // Ensure scroll event listener is added after component updates
    if (this.$refs.listContainer) {
      this.$refs.listContainer.addEventListener('scroll', this.handleScroll);
    }
  },
  watch: {
    // Watch for changes to the prop instead
    selectedType() {
      this.filterPostsByType();
    }
  }
};
</script>

<style scoped>
.posts-widget {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  width: 100%;
  text-align: center;
  flex: 1 1 0;
  min-height: 0;
  box-sizing: border-box;
  overflow-x: hidden;
}

.posts-list {
  flex: 1 1 0;
  overflow-y: auto;
  overflow-x: hidden; /* Prevent horizontal scrolling */
  padding-right: 10px;
  padding-left: 0;
  margin: 0;
  list-style: none;
  min-height: 0;
}

.list-container {
  height: 100%;
  padding-right: 5px;
  overflow-y: auto;
  overflow-x: hidden; /* Prevent horizontal scrolling */
  --fade-amount: 1.5%;
  mask-image: linear-gradient(
    transparent,
    black var(--fade-amount), 
    black calc(100% - var(--fade-amount)), 
    transparent
  );
}

.post-item {
  padding-bottom: 0.5rem;
  /* Remove border between posts */
}

.post-item:last-child {
  border-bottom: none;
}

.posts-loading,
.posts-error {
  padding: 1rem 0;
  text-align: center;
  color: #888;
}

.post-content {
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  position: relative;
  padding: 0 120px 0 0; /* Right padding for the view button */
  overflow: hidden; /* Prevent content overflow */
}

.post-left {
  display: flex;
  align-items: center;
  max-width: 50%; /* Limit the width to prevent overlap */
  min-width: 0;
}

.post-link {
  text-decoration: none;
  color: inherit;
  display: block;
  width: 100%;
  border-radius: 25px; /* Add rounded corners for hover effect */
  padding: .5rem;
  transition: background-color 0.2s ease, transform 0.3s ease; /* Add transform transition */
  box-sizing: border-box;
}

.post-link:hover {
  background-color: #f5f8fa; /* Light background on hover */
  transform: translateX(5px);
}

.post-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: #f0f0f0;
  margin-right: 1rem;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.post-icon i {
  color: #5e5d5b;
}

.post-text {
  flex: 1;
  text-align: left;
  min-width: 0;
  max-width: 100%;
}

.post-title {
  margin: 0 0 0.25rem;
  color: #5F5E5B;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.post-meta {
  font-size: 0.85em;
  color: #666;
}

.post-type-badge {
  padding: 0.35em 0;
  font-size: 0.85em;
  font-weight: 600;
  color: #94918B;
  margin: 0 auto;
  white-space: nowrap;
  width: 100px;
  text-align: center;
  position: absolute;
  left: 50%;
  transform: translateX(50%);
}

.post-right {
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
}

.view-button {
  display: inline-block;
  font-size: 0.85em;
  white-space: nowrap;
  padding-right: 1em;
  color: var(--primary-color);
}

/* Add styles for the load more indicator */
.load-more-indicator {
  text-align: center;
  padding: 1rem;
  color: #888;
  font-size: 0.9em;
}

@media (max-width: 1100px) {
  .posts-widget {
  min-height: 100px; /* Base minimum height */
  max-height: 50vh;
  flex: 1 1 auto; /* Allow growing as needed */
  }
  .posts-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .tabs {
    width: 100%;
    justify-content: flex-start;
    margin-top: 0.5rem;
  }
  
  .tabs button {
    margin-top: 0.5rem;
  }
  
  .post-type-badge {
    display: none;
  }
  
  .post-title {
    white-space: normal;
  }
  
  .post-content {
    padding-right: 70px;
  }
}

@media (max-width: 800px) {
  .post-date-circle {
    width: 40px;
    height: 40px;
    font-size: 0.9rem;
  }

  .post-left {
    max-width: 100%;
  }

  .post-type-badge {
    display: none;
  }

  .date-day {
    font-size: 1.1em;
  }

  .date-month {
    font-size: 0.75em;
  }

  .post-title {
    font-size: 1rem;
  }

  .post-meta {
    font-size: 0.75em;
  }

  .post-type-badge {
    width: 80px;
    font-size: 0.75em;
  }

  .view-button {
    font-size: 0.75em;
    padding-right: 0.75em;
  }
}
</style>
